import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { toast } from "sonner";
import { normalisePhoneGuinea, isValidPhoneGuinea } from "@/utils/phone";
import ProfilePictureUploader from "@/components/ProfilePictureUploader";
import RestaurantLocationMap from "@/components/RestaurantLocationMap";
import { RequestableField } from "@/components/RequestableField";
import { BrandLoader } from "@/components/BrandLoader";
import AlertReadinessCard from "@/components/AlertReadinessCard";
import {
  Wallet, ShieldCheck, MapPin, Bike, Truck, Store, Loader2,
  Locate, User, Phone, CheckCircle2, ReceiptText, Percent, Power,
  Camera, Image as ImageIcon, Pencil,
} from "lucide-react";

const OPERATOR_LABELS = { orange_momo: "Orange Money", mtn_momo: "MTN Mobile Money" };

// State of a requestable field: its pending request + latest rejection
function fieldState(requests, field) {
  const pending = requests.find((r) => r.field === field && r.status === "pending") || null;
  const latest = requests.find((r) => r.field === field) || null; // list is sorted desc
  const rejected = !pending && latest?.status === "rejected" ? latest : null;
  return { pending, rejected };
}

export default function PartnerProfile() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? "en-US" : "fr-FR";
  const [searchParams] = useSearchParams();
  const VALID_TABS = ["profile", "restaurant", "driver", "payouts"];
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(VALID_TABS.includes(urlTab) ? urlTab : "profile");
  const highlight = searchParams.get("highlight");
  // Deep link from a notification (?tab=payouts&highlight=NDX-...) → open the right tab
  useEffect(() => {
    const tp = searchParams.get("tab");
    if (tp && VALID_TABS.includes(tp)) setTab(tp);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [{ data: prof }, { data: pays }, { data: chg }] = await Promise.all([
        apiClient.get("/my/partner-profile"),
        apiClient.get("/my/payouts").catch(() => ({ data: [] })),
        apiClient.get("/my/change-requests").catch(() => ({ data: { items: [] } })),
      ]);
      setData(prof);
      setPayouts(pays);
      setChangeRequests(chg.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!user || !["restaurant", "driver", "admin"].includes(user.role)) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-mute">Accès réservé aux partenaires.</div>;
  }

  const isDriver = user.role === "driver";
  const isRestaurant = user.role === "restaurant" || (user.role === "admin" && !!data?.restaurant);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.25em] text-brand-red">Espace partenaire</div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-6">Mon profil partenaire</h1>

      {data && !loading && (
        <OnboardingProgress
          user={data.user}
          restaurant={data.restaurant}
          isDriver={isDriver}
          isRestaurant={isRestaurant}
        />
      )}

      {(isRestaurant || isDriver) && <AlertReadinessCard />}

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6" data-testid="partner-profile-tabs">
        {[
          { k: "profile", label: "Profil" },
          isRestaurant && { k: "restaurant", label: "Restaurant" },
          isDriver && { k: "driver", label: "Livreur" },
          { k: "payouts", label: "Paiement & reversements" },
        ].filter(Boolean).map((tt) => (
          <button
            key={tt.k}
            onClick={() => setTab(tt.k)}
            data-testid={`partner-tab-${tt.k}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold border ${tab === tt.k ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"}`}
          >{tt.label}</button>
        ))}
      </div>

      {loading || !data ? (
        <BrandLoader message="Chargement de votre profil…" />
      ) : (
        <>
          {tab === "profile" && (
            <ProfileSection user={data.user} restaurant={data.restaurant} changeRequests={changeRequests} commissionRate={data.commission_rate} livraisonPropre={data.restaurant?.livraison_propre} onSaved={refresh} />
          )}
          {tab === "restaurant" && isRestaurant && (
            <RestaurantSection restaurant={data.restaurant} onSaved={refresh} />
          )}
          {tab === "driver" && isDriver && (
            <DriverSection user={data.user} onSaved={refresh} />
          )}
          {tab === "payouts" && (
            <PayoutsSection items={payouts} dateLocale={dateLocale} highlight={highlight} user={data.user} changeRequests={changeRequests} onSaved={refresh} />
          )}
        </>
      )}
    </div>
  );
}

// ================= Profile (photo + terms) =================
function ProfileSection({ user, restaurant, changeRequests = [], commissionRate, livraisonPropre, onSaved }) {
  const [accepting, setAccepting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const lastAccepted = user?.terms_last_accepted;
  const effectiveCommission = commissionRate ?? 15;

  const saveAvatar = async (url) => {
    setSavingAvatar(true);
    try {
      await apiClient.patch("/auth/me", { picture: url || "" });
      toast.success(url ? "Photo de profil mise à jour" : "Photo retirée");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAvatar(false);
    }
  };

  const acceptTerms = async () => {
    setAccepting(true);
    try {
      await apiClient.post("/my/partner-profile/terms", {
        commission_rate: effectiveCommission,
        livraison_propre: livraisonPropre ?? null,
      });
      toast.success("Conditions acceptées et horodatées");
      setAgreed(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-section-profile">
      <Card icon={<Camera className="w-5 h-5" />} title="Photo de profil">
        <p className="text-sm text-mute mb-4">
          Votre photo apparaît sur vos commandes, votre profil et rassure vos clients.
        </p>
        <div className={savingAvatar ? "opacity-70 pointer-events-none" : ""}>
          <ProfilePictureUploader
            value={user?.picture || null}
            onChange={saveAvatar}
            label={user?.name}
            hint="JPG ou PNG, 6 Mo max"
            testId="partner-avatar"
            size={104}
          />
        </div>
      </Card>

      <Card icon={<User className="w-5 h-5" />} title="Mes informations">
        <p className="text-sm text-mute mb-2">
          Pour votre sécurité, toute modification de ces informations est <b>soumise à validation par NelDaari</b>.
          L'ancienne valeur reste active tant que la demande n'est pas validée.
        </p>
        <RequestableField
          field="name"
          label={user?.role === "restaurant" ? "Nom / raison sociale du restaurant" : "Nom complet"}
          value={user?.role === "restaurant" ? (restaurant?.name || "") : (user?.name || "")}
          {...fieldState(changeRequests, "name")}
          onChanged={onSaved}
        />
        <RequestableField field="email" label="Email" type="email" value={user?.email || ""} {...fieldState(changeRequests, "email")} onChanged={onSaved} />
        <RequestableField field="phone" label="Téléphone" type="tel" value={user?.phone || ""} {...fieldState(changeRequests, "phone")} onChanged={onSaved} />
        <RequestableField
          field="address"
          label="Adresse"
          value={user?.role === "restaurant" ? (restaurant?.address || "") : (user?.address || "")}
          {...fieldState(changeRequests, "address")}
          onChanged={onSaved}
        />
      </Card>

      <Card icon={<Percent className="w-5 h-5" />} title="Taux de commission & conditions">
        <div className="rounded-xl bg-[#FFF5F6] border border-brand-red/20 p-4 mb-4">
          <div className="text-xs uppercase tracking-widest text-brand-red mb-1">Taux appliqué à votre compte</div>
          <div className="font-display text-3xl font-semibold text-brand-red">{effectiveCommission} %</div>
          {livraisonPropre === true && (
            <p className="text-xs text-mute mt-1">✔ Livraison propre activée — vous gardez 100% des frais de livraison.</p>
          )}
          {livraisonPropre === false && (
            <p className="text-xs text-mute mt-1">Standard NelDaari — livreurs plateforme + partage des frais de livraison.</p>
          )}
        </div>

        {lastAccepted ? (
          <div className="text-sm text-mute">
            <ShieldCheck className="w-4 h-4 text-brand-green inline mr-1" />
            Dernière acceptation : <b>{new Date(lastAccepted.accepted_at).toLocaleString("fr-FR")}</b> — taux <b>{lastAccepted.commission_rate}%</b>
          </div>
        ) : (
          <>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="terms-agree"
                className="mt-1 w-4 h-4"
              />
              <span className="text-sm">
                J'accepte les <a href="/terms" target="_blank" rel="noreferrer" className="text-brand-red font-semibold underline">conditions générales</a> et le taux de commission de <b>{effectiveCommission} %</b> appliqué à mes ventes.
              </span>
            </label>
            <button
              onClick={acceptTerms}
              disabled={!agreed || accepting}
              data-testid="terms-accept"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-[#007A4A] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold"
            >
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Accepter et horodater
            </button>
          </>
        )}
      </Card>

      <PartnerDangerZone role={user?.role} />
    </div>
  );
}

// ================= Suppression de compte (restaurant & livreur) =================
function PartnerDangerZone({ role }) {
  const { logout } = useAuth();
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  if (!["restaurant", "driver"].includes(role)) return null;

  const warn = role === "restaurant"
    ? "Votre restaurant sera retiré de NelDaari et votre compte anonymisé définitivement. Vos commandes passées sont conservées pour la comptabilité. Impossible si des commandes sont en cours."
    : "Votre compte livreur sera anonymisé définitivement. Vos livraisons passées sont conservées pour la comptabilité. Impossible si une livraison est en cours ou si du cash reste à reverser.";

  const deleteAccount = async () => {
    setDeleteBusy(true);
    try {
      await apiClient.post("/auth/me/delete");
      toast.success("Votre compte a été supprimé. Au revoir !");
      logout();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleteBusy(false);
    }
  };

  return (
    <div className="rounded-2xl p-4 bg-white border border-brand-red/30" data-testid="partner-delete-account-card">
      <div className="text-xs uppercase tracking-widest text-brand-red font-bold mb-2">Zone dangereuse</div>
      {!deleteMode ? (
        <button
          onClick={() => setDeleteMode(true)}
          data-testid="partner-delete-account-btn"
          className="w-full rounded-full border border-brand-red/40 text-brand-red py-2 text-sm font-semibold hover:bg-brand-red hover:text-white transition-colors"
        >
          Supprimer mon compte
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-mute">{warn}</p>
          <p className="text-sm">Tapez <b>SUPPRIMER</b> pour confirmer :</p>
          <input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder="SUPPRIMER"
            data-testid="partner-delete-account-confirm-input"
            className="w-full bg-elevated rounded-xl px-3 py-2 text-sm outline-none border border-sand focus:border-brand-red"
          />
          <div className="flex gap-2">
            <button
              onClick={deleteAccount}
              disabled={deleteConfirm !== "SUPPRIMER" || deleteBusy}
              data-testid="partner-delete-account-confirm-btn"
              className="flex-1 rounded-full bg-brand-red text-white py-2 text-sm font-semibold disabled:opacity-40"
            >
              {deleteBusy ? "Suppression…" : "Supprimer définitivement"}
            </button>
            <button
              onClick={() => { setDeleteMode(false); setDeleteConfirm(""); }}
              data-testid="partner-delete-account-cancel"
              className="rounded-full border border-sand px-4 py-2 text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ================= Restaurant settings =================
function RestaurantSection({ restaurant, onSaved }) {
  const [livraisonPropre, setLivraisonPropre] = useState(!!restaurant?.livraison_propre);
  const [gps, setGps] = useState(restaurant?.gps || { lat: "", lng: "" });
  const [logoImage, setLogoImage] = useState(restaurant?.logo_image || "");
  const [coverImage, setCoverImage] = useState(restaurant?.cover_image || "");
  const [editableMap, setEditableMap] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingImg, setSavingImg] = useState(false);
  const [locating, setLocating] = useState(false);

  const detectGPS = () => {
    if (!("geolocation" in navigator)) { toast.error("Géolocalisation indisponible"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) });
        setLocating(false);
        toast.success("Position détectée");
      },
      () => { setLocating(false); toast.error("Impossible d'obtenir votre position"); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const saveImage = async (field, url) => {
    setSavingImg(true);
    try {
      await apiClient.patch("/my/restaurant/settings", { [field]: url || "" });
      toast.success(url ? "Image enregistrée" : "Image retirée");
      if (field === "logo_image") setLogoImage(url || "");
      if (field === "cover_image") setCoverImage(url || "");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingImg(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { livraison_propre: livraisonPropre };
      if (gps.lat !== "" && gps.lng !== "") {
        payload.gps_lat = parseFloat(gps.lat);
        payload.gps_lng = parseFloat(gps.lng);
      }
      await apiClient.patch("/my/restaurant/settings", payload);
      toast.success("Paramètres restaurant enregistrés");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!restaurant) return <div className="text-mute">Aucun restaurant lié à ce compte.</div>;

  const latNum = parseFloat(gps.lat);
  const lngNum = parseFloat(gps.lng);
  const hasValidGPS = Number.isFinite(latNum) && Number.isFinite(lngNum);

  return (
    <div className="space-y-6" data-testid="partner-section-restaurant">
      <Card icon={<ImageIcon className="w-5 h-5" />} title="Identité visuelle du restaurant">
        <p className="text-sm text-mute mb-5">
          Ajoutez un <b>logo</b> (rond, apparaît partout) et une <b>photo de couverture</b>
          {" "}(bannière large sur votre page publique).
        </p>
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${savingImg ? "opacity-70 pointer-events-none" : ""}`}>
          <ProfilePictureUploader
            value={logoImage || null}
            onChange={(url) => saveImage("logo_image", url)}
            label="Logo du restaurant"
            hint="Format carré, fond clair, 6 Mo max"
            testId="restaurant-logo"
            size={96}
            shape="circle"
          />
          <ProfilePictureUploader
            value={coverImage || null}
            onChange={(url) => saveImage("cover_image", url)}
            label="Photo de couverture"
            hint="Format paysage 16:9, plat signature"
            testId="restaurant-cover"
            size={96}
            shape="rounded"
          />
        </div>
      </Card>

      <form onSubmit={save} className="space-y-6">
        <Card icon={<Truck className="w-5 h-5" />} title="Livraison propre">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={livraisonPropre}
              onChange={(e) => setLivraisonPropre(e.target.checked)}
              data-testid="setting-livraison-propre"
              className="mt-1 w-4 h-4"
            />
            <span className="text-sm">
              <b>J'assure moi-même la livraison de mes commandes.</b><br />
              <span className="text-mute">
                Aucun livreur NelDaari ne sera affecté à vos commandes. Vous conservez 100% des frais de livraison. Commission plateforme réduite à <b>10%</b> (au lieu de 15%).
              </span>
            </span>
          </label>
        </Card>

        <Card icon={<MapPin className="w-5 h-5" />} title="Localisation GPS du restaurant">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Latitude">
              <input
                value={gps.lat || ""}
                onChange={(e) => setGps({ ...gps, lat: e.target.value })}
                placeholder="9.5150"
                inputMode="decimal"
                data-testid="setting-gps-lat"
                className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
              />
            </Field>
            <Field label="Longitude">
              <input
                value={gps.lng || ""}
                onChange={(e) => setGps({ ...gps, lng: e.target.value })}
                placeholder="-13.7050"
                inputMode="decimal"
                data-testid="setting-gps-lng"
                className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
              />
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={detectGPS}
              disabled={locating}
              data-testid="setting-gps-detect"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-sand hover:border-brand-red hover:text-brand-red px-4 py-2 text-sm font-semibold"
            >
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Locate className="w-4 h-4" />}
              Utiliser ma position actuelle
            </button>
            <button
              type="button"
              onClick={() => setEditableMap((v) => !v)}
              data-testid="setting-gps-toggle-edit"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border ${editableMap ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red hover:text-brand-red"}`}
            >
              <Pencil className="w-4 h-4" />
              {editableMap ? "Terminer l'ajustement" : "Ajuster sur la carte"}
            </button>
          </div>

          <div className="mt-4">
            <RestaurantLocationMap
              lat={hasValidGPS ? latNum : null}
              lng={hasValidGPS ? lngNum : null}
              editable={editableMap}
              onChange={(la, ln) => setGps({ lat: String(la), lng: String(ln) })}
              height={280}
            />
            {editableMap && (
              <p className="text-xs text-mute mt-2">
                💡 Cliquez sur la carte ou faites glisser l'épingle pour affiner votre emplacement exact — les livreurs vous trouveront plus vite.
              </p>
            )}
            {!hasValidGPS && (
              <p className="text-xs text-mute mt-2">
                Aucune position enregistrée — utilisez « Ma position actuelle » ou cliquez sur la carte après avoir activé l'ajustement.
              </p>
            )}
          </div>
        </Card>

        <button type="submit" disabled={saving} data-testid="restaurant-save" className="inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-6 py-3 font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Enregistrer les paramètres
        </button>
      </form>
    </div>
  );
}

// ================= Driver settings =================
function DriverSection({ user, onSaved }) {
  const [guarantorName, setGuarantorName] = useState(user?.guarantor_name || "");
  const [guarantorPhone, setGuarantorPhone] = useState(user?.guarantor_phone || "");
  const [available, setAvailable] = useState(!!user?.available);
  const [zones, setZones] = useState([]);
  const [preferredZones, setPreferredZones] = useState(user?.preferred_zones || []);
  const [savingZones, setSavingZones] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/zones").then(({ data }) => setZones(data)).catch(() => setZones([]));
  }, []);

  const toggleZone = (z) => {
    setPreferredZones((prev) => prev.includes(z) ? prev.filter((x) => x !== z) : [...prev, z]);
  };

  const savePreferredZones = async () => {
    setSavingZones(true);
    try {
      await apiClient.patch("/my/partner-profile", { preferred_zones: preferredZones });
      toast.success(preferredZones.length === 0
        ? "Toutes les zones — vous recevez les commandes de tout Conakry"
        : `Zones préférées enregistrées (${preferredZones.length})`
      );
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingZones(false);
    }
  };

  const toggleAvail = async (next) => {
    setSaving(true);
    try {
      await apiClient.patch("/my/partner-profile", { available: next });
      setAvailable(next);
      toast.success(next ? "Vous êtes en ligne" : "Vous êtes hors ligne");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveGuarantor = async (e) => {
    e.preventDefault();
    if (guarantorPhone && !isValidPhoneGuinea(guarantorPhone)) {
      toast.error("Numéro du garant invalide (format Guinée)");
      return;
    }
    setSaving(true);
    try {
      const phoneNorm = guarantorPhone ? "+" + normalisePhoneGuinea(guarantorPhone) : "";
      await apiClient.patch("/my/partner-profile", {
        guarantor_name: guarantorName.trim(),
        guarantor_phone: phoneNorm,
      });
      setGuarantorPhone(phoneNorm);
      toast.success("Garant enregistré");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-section-driver">
      <Card icon={<Power className="w-5 h-5" />} title="Statut de disponibilité">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-semibold text-lg">
              {available ? "🟢 En ligne — vous recevez des courses" : "⚫ Hors ligne — aucune course entrante"}
            </div>
            <p className="text-sm text-mute">Vous pouvez basculer à tout moment.</p>
          </div>
          <button
            type="button"
            onClick={() => toggleAvail(!available)}
            disabled={saving}
            data-testid="driver-toggle-available"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60 ${available ? "bg-mute hover:bg-[#333]" : "bg-brand-green hover:bg-[#007A4A]"}`}
          >
            {available ? "Passer hors ligne" : "Passer en ligne"}
          </button>
        </div>
      </Card>

      <Card icon={<MapPin className="w-5 h-5" />} title="Zones préférées">
        <p className="text-sm text-mute mb-3">
          Sélectionnez les quartiers où vous voulez livrer. Aucune sélection = toutes les zones de Conakry.
        </p>
        <div className="flex flex-wrap gap-2" data-testid="driver-zones-chips">
          {zones.map((z) => {
            const active = preferredZones.includes(z);
            return (
              <button
                key={z}
                type="button"
                onClick={() => toggleZone(z)}
                data-testid={`zone-chip-${z}`}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${active ? "bg-brand-green text-white border-brand-green" : "bg-white border-sand hover:border-brand-red hover:text-brand-red"}`}
              >
                {active ? "✓ " : ""}{z}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={savePreferredZones}
          disabled={savingZones}
          data-testid="driver-zones-save"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold"
        >
          {savingZones ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Enregistrer les zones
        </button>
      </Card>

      <Card icon={<User className="w-5 h-5" />} title="Personne garante">
        <p className="text-sm text-mute mb-4">
          Nous demandons un garant (proche, parent, employeur) qui répond de vous en cas de problème. Cette information reste strictement confidentielle.
        </p>
        <form onSubmit={saveGuarantor} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nom complet du garant">
            <input
              value={guarantorName}
              onChange={(e) => setGuarantorName(e.target.value)}
              placeholder="Ex. Mamadou Diallo"
              required
              data-testid="driver-guarantor-name"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
            />
          </Field>
          <Field label="Téléphone du garant">
            <input
              type="tel"
              value={guarantorPhone}
              onChange={(e) => setGuarantorPhone(e.target.value)}
              placeholder="+224 6XX XX XX XX"
              required
              data-testid="driver-guarantor-phone"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
            />
          </Field>
          <div className="md:col-span-2">
            <button type="submit" disabled={saving} data-testid="driver-guarantor-save" className="inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-6 py-3 font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// ================= Payouts history =================
// ================= Payout account (Paiement & reversements) =================
function PayoutAccountCard({ user, changeRequests = [], onSaved }) {
  const initial = user?.payout || { operator: "orange_momo", number: "", holder_name: "" };
  const [payout, setPayout] = useState(initial);
  const [saving, setSaving] = useState(false);
  const configured = !!(user?.payout && user.payout.number);
  const hasPendingPayout = changeRequests.some((r) => r.status === "pending" && r.field.startsWith("payout_"));

  const savePayout = async (e) => {
    e.preventDefault();
    if (payout.number && !isValidPhoneGuinea(payout.number)) {
      toast.error("Numéro Mobile Money invalide (format Guinée)");
      return;
    }
    setSaving(true);
    try {
      const clean = { ...payout, number: payout.number ? "+" + normalisePhoneGuinea(payout.number) : payout.number };
      const { data } = await apiClient.patch("/my/partner-profile", { payout: clean });
      setPayout(clean);
      toast.success("Compte de reversement enregistré");
      if (data?.holder_warning) {
        toast.warning(data.holder_warning, { duration: 10000 });
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Account already configured → every change goes through admin validation
  if (configured) {
    return (
      <Card icon={<Wallet className="w-5 h-5" />} title="Compte de reversement">
        <p className="text-sm text-mute mb-1">
          NelDaari verse vos gains sur ce compte mobile money (express H+1 pour les paiements en ligne, chaque lundi pour le reste).
        </p>
        <p className="text-xs text-brand-red font-semibold mb-2">
          🔒 Sécurité renforcée : toute modification est soumise à validation NelDaari et <b>suspend vos reversements jusqu&apos;à validation</b>.
        </p>
        {hasPendingPayout && (
          <div className="rounded-xl bg-brand-red/10 border border-brand-red/30 px-3 py-2 text-xs font-semibold text-brand-red mb-2" data-testid="payout-suspended-banner">
            ⚠️ Vos reversements sont suspendus — une modification du compte est en attente de validation.
          </div>
        )}
        <RequestableField
          field="payout_operator"
          label="Opérateur"
          value={user.payout.operator || ""}
          display={OPERATOR_LABELS[user.payout.operator] || user.payout.operator}
          options={Object.entries(OPERATOR_LABELS).map(([value, label]) => ({ value, label }))}
          {...fieldState(changeRequests, "payout_operator")}
          onChanged={onSaved}
        />
        <RequestableField field="payout_number" label="Numéro du compte" type="tel" value={user.payout.number || ""} {...fieldState(changeRequests, "payout_number")} onChanged={onSaved} />
        <RequestableField field="payout_holder" label="Nom du titulaire" value={user.payout.holder_name || ""} {...fieldState(changeRequests, "payout_holder")} onChanged={onSaved} />
      </Card>
    );
  }

  // First-time setup: direct save allowed
  return (
    <Card icon={<Wallet className="w-5 h-5" />} title="Compte de reversement">
      <p className="text-sm text-mute mb-4">
        NelDaari verse vos gains sur le compte mobile money indiqué ci-dessous (express H+1 pour les paiements en ligne, chaque lundi pour le reste).
      </p>
      <form onSubmit={savePayout} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Opérateur">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(OPERATOR_LABELS).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setPayout({ ...payout, operator: v })}
                data-testid={`payout-op-${v}`}
                className={`rounded-xl px-3 py-3 text-sm font-semibold border transition-colors ${payout.operator === v ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"}`}
              >{l}</button>
            ))}
          </div>
        </Field>
        <Field label="Numéro du compte">
          <input
            type="tel"
            value={payout.number || ""}
            onChange={(e) => setPayout({ ...payout, number: e.target.value })}
            placeholder="+224 6XX XX XX XX"
            required
            data-testid="payout-number"
            className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
          />
        </Field>
        <div className="md:col-span-2">
          <Field label="Nom du titulaire (distinct du numéro de contact)">
            <input
              value={payout.holder_name || ""}
              onChange={(e) => setPayout({ ...payout, holder_name: e.target.value })}
              placeholder="Nom exactement comme sur le compte MoMo"
              required
              minLength={2}
              data-testid="payout-holder"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
            />
          </Field>
        </div>
        <div className="md:col-span-2">
          <button type="submit" disabled={saving} data-testid="payout-save" className="inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-6 py-3 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </form>
    </Card>
  );
}

function PayoutsSection({ items, dateLocale, highlight, user, changeRequests = [], onSaved }) {
  const [pending, setPending] = useState(null);
  useEffect(() => {
    apiClient.get("/my/ledger/balance").then(({ data }) => setPending(data)).catch(() => {});
  }, []);
  // Deep link: scroll to and flash the highlighted payout row
  useEffect(() => {
    if (!highlight || items.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`payout-row-${highlight}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("deep-link-highlight");
        setTimeout(() => el.classList.remove("deep-link-highlight"), 3200);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [highlight, items]);
  const pendingAmount = pending
    ? (pending.role === "restaurant" ? pending.net : pending.solde_net)
    : null;
  const total = items.reduce((s, p) => s + (p.amount || 0), 0);
  return (
    <div className="space-y-4" data-testid="payouts-list">
      {pendingAmount !== null && (
        <div className="rounded-2xl border-2 border-brand-green/30 bg-[#E7F6EE] p-4 flex items-center justify-between flex-wrap gap-3" data-testid="payouts-pending">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">Solde à reverser (prochain lundi)</div>
            <div className="font-display text-3xl font-bold text-brand-green">{formatGNF(Math.max(0, pendingAmount))}</div>
            {pending?.role === "driver" && pending.cash_a_reverser > 0 && (
              <div className="text-xs text-mute mt-1">
                Dont cash encaissé à reverser : <b className="text-brand-red">{formatGNF(pending.cash_a_reverser)}</b>
              </div>
            )}
          </div>
          <div className="text-xs text-mute max-w-xs">
            Commandes payées en ligne : reversées automatiquement en <b>express (H+1)</b>. Le reste est reversé chaque lundi à 08:00 sur le compte enregistré dans votre profil.
          </div>
        </div>
      )}

      <PayoutAccountCard user={user} changeRequests={changeRequests} onSaved={onSaved} />

      {items.length === 0 ? (
        <div className="bg-white border border-sand rounded-2xl p-10 text-center" data-testid="payouts-empty">
          <ReceiptText className="w-10 h-10 text-mute mx-auto mb-3" />
          <h3 className="font-display text-xl font-semibold mb-1">Aucun virement reçu</h3>
          <p className="text-mute text-sm">Vos virements (express H+1 et hebdomadaires) apparaîtront ici.</p>
        </div>
      ) : (
      <div className="bg-white border border-sand rounded-2xl p-4" data-testid="partner-payouts-received">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-xs uppercase tracking-widest text-mute">Virements reçus</div>
          <div className="text-xs text-mute">
            {items.length} virement(s) · total <b className="text-ink">{formatGNF(total)}</b>
          </div>
        </div>
        <div className="space-y-1 max-h-[28rem] overflow-y-auto pr-1">
          {items.map((p) => (
            <div
              key={p.payout_id}
              id={`payout-row-${p.payout_id}`}
              data-testid={`payout-row-${p.payout_id}`}
              className="flex items-center justify-between gap-2 py-2.5 px-2 rounded-xl border-b border-sand/60 last:border-0"
            >
              <div className="min-w-0">
                <div className="font-display font-bold text-brand-green">{formatGNF(p.amount)}</div>
                <div className="text-[11px] text-mute truncate">
                  {OPERATOR_LABELS[p.operator] || p.operator} · Réf : {p.reference || p.payout_id}
                </div>
                {p.express && p.order_id && (
                  <Link
                    to={user?.role === "driver"
                      ? `/driver?tab=history&highlight=${p.order_id}`
                      : `/dashboard/restaurant?tab=orders&highlight=${p.order_id}`}
                    data-testid={`payout-view-order-${p.payout_id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-red underline underline-offset-2 mt-0.5"
                  >
                    Voir la commande #{p.order_id.slice(-6).toUpperCase()}
                  </Link>
                )}
              </div>
              <div className="text-right shrink-0">
                {p.express ? (
                  <span className="inline-block rounded-full bg-[#FF7900]/15 text-[#C05F00] px-2 py-0.5 text-[10px] font-bold mb-0.5" data-testid={`payout-express-${p.payout_id}`}>⚡ Express H+1</span>
                ) : (
                  <span className="inline-block rounded-full bg-elevated text-mute px-2 py-0.5 text-[10px] font-bold mb-0.5">Hebdo</span>
                )}
                <div className="text-[11px] text-mute">
                  {p.paid_at ? new Date(p.paid_at).toLocaleString(dateLocale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

// ================= Helpers =================
function Card({ icon, title, children }) {
  return (
    <div className="bg-white border border-sand rounded-2xl p-5 sm:p-6">
      <h3 className="font-display text-xl font-semibold mb-3 flex items-center gap-2">
        <span className="text-brand-red">{icon}</span>{title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-mute block mb-2">{label}</span>
      {children}
    </label>
  );
}

// ================= Onboarding Progress =================
function OnboardingProgress({ user, restaurant, isDriver, isRestaurant }) {
  const items = [];
  const hasPayout = !!(user?.payout?.number && user?.payout?.holder_name);
  const hasTerms = !!user?.terms_last_accepted;
  items.push({ k: "payout", label: "Compte MoMo", done: hasPayout });
  items.push({ k: "terms", label: "CGU signées", done: hasTerms });
  if (isRestaurant) {
    items.push({ k: "gps", label: "GPS restaurant", done: !!(restaurant?.gps?.lat && restaurant?.gps?.lng) });
  }
  if (isDriver) {
    items.push({ k: "guarantor", label: "Garant", done: !!(user?.guarantor_name && user?.guarantor_phone) });
  }
  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / items.length) * 100);
  const complete = done === items.length;

  return (
    <div
      className={`rounded-2xl p-5 mb-6 border ${complete ? "bg-[#E7F6EE] border-brand-green/30" : "bg-white border-sand"}`}
      data-testid="onboarding-progress"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-red font-semibold">
            {complete ? "Profil complet" : "Complétez votre profil"}
          </div>
          <div className="font-display text-xl font-semibold">
            {done}/{items.length} · {pct} %
          </div>
          {complete ? (
            <p className="text-sm text-brand-green mt-0.5">🎉 Bonus <b>0% de commission</b> sur votre 1<sup>ère</sup> semaine activée.</p>
          ) : (
            <p className="text-sm text-mute mt-0.5">Chaque étape franchie débloque un avantage partenaire.</p>
          )}
        </div>
      </div>
      <div className="h-2 rounded-full bg-elevated overflow-hidden mb-4">
        <div
          className={`h-full transition-[width] duration-500 ${complete ? "bg-brand-green" : "bg-brand-red"}`}
          style={{ width: `${pct}%` }}
          data-testid="onboarding-bar"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <span
            key={it.k}
            data-testid={`onboarding-badge-${it.k}${it.done ? "-done" : ""}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${it.done ? "bg-brand-green text-white" : "bg-elevated text-mute"}`}
          >
            {it.done ? "✓" : "○"} {it.label}
          </span>
        ))}
      </div>
    </div>
  );
}
